import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core/PageData'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../../util/Functions'
import {CustomInputRenderer} from '../../../../common/component/CustomInputRenderer'
import { array, object } from 'yup'
import { IFilterDto } from '../../../../common/model/Core.model'
import { ClientListItem } from '../../../client/components/ClientListItem'
import { TagInputSearchListControls } from '../../../../common/component/TagInputSearchListControls'
import classNames from 'classnames'
import { IClientDto, IClientFilter } from '../../../client/Client.model'
import { useClientGetByIdMutation, useClientSearchMutation } from '../../../client/Client.api'
import 'flatpickr/dist/themes/light.css'
import { COUNTRIES_EN } from '../../../../util/Constants'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { FilePicker } from '../../../../common/component/FilePicker'
import { IAttachment } from '../../../media/Media.model'
import { MessageToast, MessageType } from '../../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { IContactDto } from '../../../contact/Contact.model'
import FormValidator from '../../../../util/FormValidator.util'
import { getCriticality, ImpactLevelOptions, ITicketCreateRequestDto, mapCriticalityLabelColor, PriorityLevelOptions, TicketStatus } from '../Ticket.model'
import { useTicketCreateMutation } from '../Ticket.api'
import { IAssetDto, IAssetFilter } from '../../../asset/Asset.model'
import { useAssetGetByIdMutation, useAssetSearchMutation } from '../../../asset/Asset.api'
import { AssetListItem } from '../../../asset/components/AssetListItem'
import { IconLabelContent } from '../../../../common/component/IconLabelContent'
import { ContactPicker } from '../../../contact/components/ContactPicker'
import { ContactListItem } from '../../../contact/components/ContactListItem'
import { DateTimeInputPicker } from '../../../../common/component/DateTimeInputPicker'
import { ContractEmbedered } from '../../../contract/components/ContractEmbedered'

interface ICreateFormikSchema {
  clientId: string
  assetId?: string
  reportedAt: string
  reportedById: string
  statusCode: string
  status: string
  impactCode: string
  impact: string
  priorityCode: string
  priority: string
  criticalityCode?: string
  criticality?: string
  title: string
  description?: string
  notes?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  attachments: IAttachment[]
}

export const TicketNewPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search)
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.TICKETS'}),
      path: '/tickets',
      isSeparator: false,
      isActive: false,
    },
  ]

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      clientId: formValidator.validateStringStrict(),
      assetId: formValidator.validateString(),
      reportedAt: formValidator.validateStringStrict(),
      reportedById: formValidator.validateStringStrict(),
      impactCode: formValidator.validateStringStrict(),
      priorityCode: formValidator.validateStringStrict(),
      title: formValidator.validateStringStrict(),
      description: formValidator.validateTextArea(),
      notes: formValidator.validateTextArea(),
      countryCode: formValidator.validateString(),
      country: formValidator.validateString(),
      address: formValidator.validateString(),
      latitude: formValidator.validateString(),
      longitude: formValidator.validateString(),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
    })
  )
  const [initialValues, setInitialValues] = useState<ICreateFormikSchema>({
    clientId: '',
    reportedAt: '',
    reportedById: '',
    statusCode: TicketStatus.NEW,
    status: intl.formatMessage({id: `TICKET.STATUS.${TicketStatus.NEW}`}),
    impactCode: '',
    impact: '',
    priorityCode: '',
    priority: '',
    title: '',
    attachments: [],
  })

  const clientFilterKeys = {
    defaultKey: IClientFilter.NAME,
    defaultKeyList: [
      IClientFilter.ID,
      IClientFilter.NAME,
      IClientFilter.SECTOR,
      IClientFilter.ADDRESS,
    ],
  }
  const didFetchClient = useRef(false)
  const [clientFilterSuggestions, setClientFilterSuggestions] = useState<string[]>(clientFilterKeys.defaultKeyList)
  const [clientFilters, setClientFilters] = useState<IFilterDto[]>([])
  const [client, setClient] = useState<IClientDto | undefined>()
  const [isClientLoading, setIsClientLoading] = useState(false)
  const [clientSearchResult, setClientSearchResult] = useState<IClientDto[] | undefined>(undefined)
  const [clientGetById] = useClientGetByIdMutation()
  const [clientSearch] = useClientSearchMutation()

  const assetFilterKeys = {
    defaultKey: IAssetFilter.CLIENT,
    defaultKeyList: [
      IAssetFilter.ID,
      IAssetFilter.FAMILY,
      IAssetFilter.AREA,
      IAssetFilter.BRAND,
      IAssetFilter.MODEL,
      IAssetFilter.SERIAL,
      IAssetFilter.ADDRESS,
    ],
  }
  const didFetchAsset = useRef(false)
  const [assetFilterSuggestions, setAssetFilterSuggestions] = useState<string[]>(assetFilterKeys.defaultKeyList)
  const [assetFilters, setAssetFilters] = useState<IFilterDto[]>([])
  const [asset, setAsset] = useState<IAssetDto | undefined>(undefined)
  const [isAssetLoading, setIsAssetLoading] = useState(false)
  const [assetSearchResult, setAssetSearchResult] = useState<IAssetDto[] | undefined>(undefined)
  const [assetGetById] = useAssetGetByIdMutation()
  const [assetSearch] = useAssetSearchMutation()

  const [contact, setContact] = useState<IContactDto | undefined>(undefined) 
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)
  const [ticketCreate] = useTicketCreateMutation();

  const formik = useFormik<ICreateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)
          const request: ITicketCreateRequestDto = {
            clientId: values.clientId,
            assetId: values.assetId,
            reportedAt: values.reportedAt,
            reportedBy: values.reportedById,
            statusCode: values.statusCode,
            status: values.status,
            impactCode: values.impactCode,
            impact: intl.formatMessage({id: `TICKET.IMPACT.${values.impactCode}`}),
            priorityCode: values.priorityCode,
            priority: intl.formatMessage({
              id: `TICKET.PRIORITY.${values.priorityCode}`,
            }),
            criticalityCode: getCriticality(formik.values.impactCode, formik.values.priorityCode),
            criticality: intl.formatMessage({
              id: `TICKET.CRITICALITY.${getCriticality(
                formik.values.impactCode,
                formik.values.priorityCode
              )}`,
            }),
            title: values.title,
            description: values.description,
            notes: values.notes,
            countryCode: values.countryCode,
            country: values.countryCode
              ? intl.formatMessage({
                  id: `DROPDOWN.COUNTRY.${values.countryCode}`,
                })
              : undefined,
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            attachments: values.attachments,
          }

          const response: any = await ticketCreate(request).unwrap()
          if (response && response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/tickets/${response.ticket.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setClientSearchResult(undefined)
        setAssetSearchResult(undefined)
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchClient = async (clientId) => {
      try {
        if (!didFetchClient.current) {
          setIsClientLoading(true)
          const response: any = await clientGetById({id: clientId, fields: 'LIMITED'})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const client: IClientDto = response.data
            setClient(client)
            setClientSearchResult(undefined)

            formik.setFieldValue('clientId', client.id)

            setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: client.id, editable: false}])
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsClientLoading(false)
      }
    }
    
    const fetchAsset = async (assetId) => {
      try {
        if (!didFetchAsset.current) {
          setIsAssetLoading(true)
          const response: any = await assetGetById({id: assetId, fields: 'LIMITED'})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const asset: IAssetDto = response.data
            setAsset(asset)
            setAssetSearchResult(undefined)

            formik.setFieldValue('assetId', asset.id)
            formik.setFieldValue('countryCode', asset.countryCode)
            formik.setFieldValue('address', asset.address)

            fetchClient(asset.client.id)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAssetLoading(false)
      }
    }

    const clientId = queryParams.get('clientId')
    const assetId = queryParams.get('assetId')
    if (assetId) {
      fetchAsset(assetId)
    } else if (clientId) {
      fetchClient(clientId)
    }
  }, [])

  const clientRowOnClickHandler = async (id) => {
    if (clientSearchResult) {
      const client = clientSearchResult.find((client) => client.id === id)
      if (client) {
        setClient(client)
        setClientSearchResult(undefined)
        formik.setFieldValue('clientId', client.id)
        setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: client.id, editable: false}])
      }
    }
  }

  const clientRemoveOnClickHandler = async () => {
    setClient(undefined)
    setClientSearchResult(undefined)
    setClientFilters([])
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

    setAsset(undefined)
    setAssetSearchResult(undefined)
    setAssetFilters([])
    setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)

    formik.setFieldValue('clientId', '')
    formik.setFieldValue('assetId', '')
    formik.setFieldValue('countryCode', '')
    formik.setFieldValue('address', '')
  }

  const clientSearchOnClickHandler = async () => {
    try {
      if (!didFetchClient.current) {
        setIsClientLoading(true)
        const response: any = await clientSearch({filters: clientFilters})
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setClientSearchResult(response.data.data)
          setClientFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsClientLoading(false)
    }
  }

  const clientResetSearchResetOnClickHandler = async () => {
    setClientSearchResult(undefined)
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)
  }

  const assetRowOnClickHandler = async (assetId) => {
    if (assetSearchResult) {
      const asset = assetSearchResult.find((asset) => asset.id === assetId)
      if (asset) {
        setAsset(asset)
        setAssetSearchResult(undefined)

        formik.setFieldValue('assetId', asset.id)
        formik.setFieldValue('countryCode', asset.countryCode)
        formik.setFieldValue('address', asset.address)
      }
    }
  }

  const assetRemoveOnClickHandler = async () => {
    setAsset(undefined)
    setAssetSearchResult(undefined)

    if (client) {
      setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: client.id, editable: false}])
      setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)
    }

    formik.setFieldValue('assetId', '')
    formik.setFieldValue('contractId', '')
    formik.setFieldValue('countryCode', '')
    formik.setFieldValue('address', '')
  }

  const assetSearchOnClickHandler = async () => {
    try {
      if (client && !didFetchAsset.current) {
        setIsAssetLoading(true)
        const response: any = await assetSearch({filters: assetFilters})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setAssetSearchResult(response.data.data)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsAssetLoading(false)
    }
  }

  const assetResetSearchResetOnClickHandler = async () => {
    setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)
    setAssetSearchResult(undefined)
  }

  const contactSelectOnClickHandler = async (contact) => {
    if (contact) {
      setContact(contact)
      formik.setFieldValue('reportedById', contact.id)
    }
  }

  const contactRemoveOnClickHandler = async () => {
    setContact(undefined)
    formik.setFieldValue('reportedById', '')
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.clientId && formik.errors.clientId) ||
          (formik.touched.reportedAt && formik.errors.reportedAt) ||
          (formik.touched.reportedById && formik.errors.reportedById) ||
          (formik.touched.impactCode && formik.errors.impactCode) ||
          (formik.touched.priorityCode && formik.errors.priorityCode) ||
          (formik.touched.title && formik.errors.title)
        )
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>
                          {intl.formatMessage({id: 'UTIL.CREATE.TICKET'})}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary me-2 d-none d-md-block'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-floppy'></i>
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'UTIL.SAVE'})}
                            </span>
                          </>
                        )}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-floppy'></i>
                        )}
                      </button>

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/tickets`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {tabWithError('kt_tab_pane_1') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      {!client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span
                              className={classNames('required', {
                                'text-gray-700': !(
                                  formik?.touched?.clientId && formik?.errors?.clientId
                                ),
                                'text-danger':
                                  formik?.touched?.clientId && formik?.errors?.clientId,
                              })}
                            >
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <TagInputSearchListControls
                              loading={isClientLoading || isAssetLoading}
                              enabled={!formik.isSubmitting}
                              filterKeys={clientFilterKeys}
                              filterSuggestions={clientFilterSuggestions}
                              filters={clientFilters}
                              createOnClickPath={'/clients/new'}
                              setFilterSuggestions={setClientFilterSuggestions}
                              setFilters={setClientFilters}
                              searchOnClickHandler={clientSearchOnClickHandler}
                              resetSearchResetOnClickHandler={clientResetSearchResetOnClickHandler}
                            ></TagInputSearchListControls>
                          </div>
                          <label className='col-xxl-3'></label>
                          <div className='col-xxl-9 h-3px'>
                            {clientSearchResult && clientSearchResult?.length > 0 && (
                              <ul
                                id='report-client-autocomplete-list'
                                className='suggestions-list'
                                role='listbox'
                              >
                                {clientSearchResult.map((item, index) => (
                                  <li key={index} onClick={() => clientRowOnClickHandler(item.id)}>
                                    <ClientListItem data={item}></ClientListItem>
                                    <div className='separator'></div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </>
                      )}

                      {client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span className='text-gray-700 required'>
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <ClientListItem
                              disabled={formik.isSubmitting}
                              data={client}
                              rowSelected={client.id}
                              removeOnClick={clientRemoveOnClickHandler}
                            ></ClientListItem>
                          </div>
                        </>
                      )}
                    </div>

                    {client && (
                      <>
                        <div className='row mb-5'>
                          {!asset && (
                            <>
                              <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                <span
                                  className={classNames('', {
                                    'text-gray-700': !(
                                      formik?.touched?.assetId && formik?.errors?.assetId
                                    ),
                                    'text-danger':
                                      formik?.touched?.assetId && formik?.errors?.assetId,
                                  })}
                                >
                                  {intl.formatMessage({id: 'UTIL.ASSET'})}
                                </span>
                              </label>

                              <div className='col-xxl-9'>
                                <TagInputSearchListControls
                                  loading={isAssetLoading || isClientLoading}
                                  enabled={!formik.isSubmitting}
                                  filterKeys={assetFilterKeys}
                                  filterSuggestions={assetFilterSuggestions}
                                  filters={assetFilters}
                                  createOnClickPath={`/assets/new?clientId=${client.id}`}
                                  setFilterSuggestions={setAssetFilterSuggestions}
                                  setFilters={setAssetFilters}
                                  searchOnClickHandler={assetSearchOnClickHandler}
                                  resetSearchResetOnClickHandler={
                                    assetResetSearchResetOnClickHandler
                                  }
                                ></TagInputSearchListControls>
                              </div>
                              <label className='col-xxl-3'></label>
                              <div className='col-xxl-9 h-3px'>
                                {assetSearchResult && assetSearchResult.length > 0 && (
                                  <ul
                                    id='autocomplete-list'
                                    className='suggestions-list'
                                    role='listbox'
                                  >
                                    {assetSearchResult.map((asset, index) => (
                                      <li
                                        key={index}
                                        onClick={() => assetRowOnClickHandler(asset.id)}
                                      >
                                        <AssetListItem
                                          data={asset}
                                          rowSelected={asset.id}
                                        ></AssetListItem>
                                        <div className='separator'></div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </>
                          )}

                          {asset && (
                            <>
                              <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                <span className='text-gray-700'>
                                  {intl.formatMessage({id: 'UTIL.ASSET'})}
                                </span>
                              </label>

                              <div className='col-xxl-9'>
                                <AssetListItem
                                  disabled={formik.isSubmitting}
                                  data={asset}
                                  rowSelected={asset.id}
                                  removeOnClick={assetRemoveOnClickHandler}
                                ></AssetListItem>
                              </div>
                            </>
                          )}
                        </div>

                        {asset && asset.contract && <ContractEmbedered contract={asset.contract} />}
                      </>
                    )}

                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.GENERAL'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.TITLE'})}
                            required={true}
                            placeholder={intl.formatMessage({id: 'TICKET.TITLE'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName={`title`}
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CRITICALITY_LEVEL'})}
                            object={
                              <>
                                <span
                                  className={mapCriticalityLabelColor(
                                    getCriticality(
                                      formik.values.impactCode,
                                      formik.values.priorityCode
                                    )
                                  )}
                                >
                                  {intl.formatMessage({
                                    id: `TICKET.CRITICALITY.${getCriticality(
                                      formik.values.impactCode,
                                      formik.values.priorityCode
                                    )}`,
                                  })}
                                </span>
                                <IconLabelContent
                                  title={intl.formatMessage({
                                    id: `TICKET.CRITICALITY.${getCriticality(
                                      formik.values.impactCode,
                                      formik.values.priorityCode
                                    )}.LABEL`,
                                  })}
                                  icon={'bi bi-info-lg'}
                                  active={true}
                                  activeColor={'text-gray-400'}
                                />
                              </>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.IMPACT_LEVEL'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='impactCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('impactCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                                title={intl.formatMessage({
                                  id: 'TICKET.IMPACT_AND_PRIORITY.REQUIRED',
                                })}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {ImpactLevelOptions &&
                                  ImpactLevelOptions.map((impactLevel, index) => (
                                    <option key={index} value={impactLevel}>
                                      {intl.formatMessage({id: `TICKET.IMPACT.${impactLevel}`})}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.PRIORITY_LEVEL'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='priorityCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('priorityCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                                title={intl.formatMessage({
                                  id: 'TICKET.IMPACT_AND_PRIORITY.REQUIRED',
                                })}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {PriorityLevelOptions &&
                                  PriorityLevelOptions.map((priorityLevel, index) => (
                                    <option key={index} value={priorityLevel}>
                                      {intl.formatMessage({
                                        id: `TICKET.PRIORITY.${priorityLevel}`,
                                      })}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <DateTimeInputPicker
                            label={intl.formatMessage({id: 'UTIL.REPORTED_DATE_TIME'})}
                            required={true}
                            placeholder={intl.formatMessage({id: 'UTIL.REPORTED_DATE_TIME'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='reportedAt'
                          ></DateTimeInputPicker>

                          {!contact && (
                            <div className='row my-5'>
                              <ContactPicker
                                label={intl.formatMessage({id: 'UTIL.REPORTED_BY'})}
                                required={true}
                                enabled={!formik.isSubmitting}
                                selectOnClick={contactSelectOnClickHandler}
                              ></ContactPicker>
                            </div>
                          )}

                          {contact && (
                            <div className='row my-5'>
                              <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                <span className='text-gray-700 required'>
                                  {intl.formatMessage({id: 'UTIL.REPORTED_BY'})}
                                </span>
                              </label>

                              <div className='col-xxl-9'>
                                <ContactListItem
                                  disabled={formik.isSubmitting}
                                  data={contact}
                                  rowSelected={contact.id}
                                  removeOnClick={contactRemoveOnClickHandler}
                                ></ContactListItem>
                              </div>
                            </div>
                          )}

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='description'
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                placeholder={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                                {...formik.getFieldProps('description')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              />
                            }
                            enableInputLength={true}
                            inputLength={3000}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.NOTES'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='notes'
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                placeholder={intl.formatMessage({id: 'TICKET.NOTES'})}
                                {...formik.getFieldProps('notes')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              />
                            }
                            enableInputLength={true}
                            inputLength={3000}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.LOCATION'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='countryCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('countryCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {COUNTRIES_EN &&
                                  COUNTRIES_EN.map((country, index) => (
                                    <option key={index} value={country.code}>
                                      {intl.formatMessage({id: `DROPDOWN.COUNTRY.${country.code}`})}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            required={false}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='address'
                            enableInputLength={true}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <FilePicker
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='attachments'
                            setIsAttachmentProcessing={setIsAttachmentProcessing}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}
